import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MainMatchesPrevisionData } from './MainMatchesData';

/**
 * Minimal snapshot of a match used inside the "betting slip" selection.
 * Stored globally so it survives navigation across pages.
 */
export interface SelectedMatch {
  MatchId: number;
  Country: string;
  MatchDate: string;
  League: string;
  Home: string;
  Away: string;
  PredictionType: string;
  ExactResult: string;
  Prob: number;
  Odd: number;
}

/**
 * Unique key for a selection entry. The same match can be selected for
 * multiple bet types, so the key combines match, bet type and exact result.
 */
export const getSelectionKey = (match: SelectedMatch): string =>
  `${match.MatchId}|${match.PredictionType}|${match.ExactResult ?? ''}`;

/** Convert a full prediction row into the minimal selection shape. */
export const toSelectedMatch = (
  match: MainMatchesPrevisionData,
): SelectedMatch => ({
  MatchId: match.MatchId,
  Country: match.Country,
  MatchDate: match.MatchDate,
  League: match.League,
  Home: match.Home,
  Away: match.Away,
  PredictionType: match.PredictionType,
  ExactResult: match.ExactResult,
  Prob: match.Prob,
  Odd: match.Odd,
});

interface SelectionContextValue {
  selectedMatches: SelectedMatch[];
  isCollapsed: boolean;
  isSelected: (match: SelectedMatch) => boolean;
  toggleMatch: (match: SelectedMatch) => void;
  removeMatch: (match: SelectedMatch) => void;
  clearSelection: () => void;
  toggleCollapsed: () => void;
}

const STORAGE_KEY = 'betzen_selected_matches';

const SelectionContext = createContext<SelectionContextValue | undefined>(
  undefined,
);

const readStoredSelection = (): SelectedMatch[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (m) => m && typeof m.MatchId === 'number' && m.Home && m.Away,
    );
  } catch {
    return [];
  }
};

export const SelectionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedMatches, setSelectedMatches] =
    useState<SelectedMatch[]>(readStoredSelection);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedMatches));
    } catch {
      // Storage may be unavailable (private mode); selection still works in memory.
    }
  }, [selectedMatches]);

  const isSelected = useCallback(
    (match: SelectedMatch) =>
      selectedMatches.some(
        (m) => getSelectionKey(m) === getSelectionKey(match),
      ),
    [selectedMatches],
  );

  const toggleMatch = useCallback((match: SelectedMatch) => {
    const key = getSelectionKey(match);
    setSelectedMatches((prev) =>
      prev.some((m) => getSelectionKey(m) === key)
        ? prev.filter((m) => getSelectionKey(m) !== key)
        : [...prev, match],
    );
  }, []);

  const removeMatch = useCallback((match: SelectedMatch) => {
    const key = getSelectionKey(match);
    setSelectedMatches((prev) =>
      prev.filter((m) => getSelectionKey(m) !== key),
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMatches([]);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      selectedMatches,
      isCollapsed,
      isSelected,
      toggleMatch,
      removeMatch,
      clearSelection,
      toggleCollapsed,
    }),
    [
      selectedMatches,
      isCollapsed,
      isSelected,
      toggleMatch,
      removeMatch,
      clearSelection,
      toggleCollapsed,
    ],
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = (): SelectionContextValue => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};
